/**
 * Vue Composable for DiabAssist Notifications
 */

import { ref, onMounted, onUnmounted, computed } from 'vue'
import {
  initNotificationService,
  provisionNotifications,
  loadSavedConfig,
  connect,
  disconnect,
  clearNotifications,
  onNotification,
  onConnectionChange,
  getConnectionStatus,
  getNotificationHistory,
} from '@/services/notificationService'
import { isAuthenticated } from '@/lib/keycloak'

interface Notification {
  id: number
  title: string
  message: string
  priority: number
  date: string
  type?: string
  read?: boolean
}

export function useNotifications() {
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const isConnected = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  let unsubscribeNotification: (() => void) | null = null
  let unsubscribeConnection: (() => void) | null = null

  // Initialize and connect
  async function initialize(): Promise<void> {
    if (!isAuthenticated()) {
      console.log('[useNotifications] Not authenticated, skipping initialization')
      return
    }

    isLoading.value = true
    error.value = null

    try {
      await initNotificationService()

      // Try to load saved config first
      const hasSavedConfig = loadSavedConfig()

      if (!hasSavedConfig) {
        // Provision new config
        const success = await provisionNotifications()
        if (!success) {
          error.value = 'Failed to provision notifications'
          return
        }
      }

      // Connect to WebSocket
      connect()

      // Load notification history
      await loadHistory()
    } catch (err) {
      console.error('[useNotifications] Initialization error:', err)
      error.value = 'Failed to initialize notifications'
    } finally {
      isLoading.value = false
    }
  }

  async function loadHistory(): Promise<void> {
    try {
      const history = await getNotificationHistory()
      notifications.value = history.map(n => ({
        id: n.id,
        title: n.title,
        message: n.message,
        priority: n.priority,
        date: n.date,
        type: n.extras?.type as string | undefined,
        read: false,
      }))
      updateUnreadCount()
    } catch (err) {
      console.error('[useNotifications] Failed to load history:', err)
    }
  }

  function handleNewNotification(notification: {
    id: number
    title: string
    message: string
    priority: number
    date: string
    extras?: { type?: string }
  }): void {
    // Add to beginning of list
    notifications.value.unshift({
      id: notification.id,
      title: notification.title,
      message: notification.message,
      priority: notification.priority,
      date: notification.date,
      type: notification.extras?.type,
      read: false,
    })

    // Keep only last 50
    if (notifications.value.length > 50) {
      notifications.value = notifications.value.slice(0, 50)
    }

    updateUnreadCount()
  }

  function handleConnectionChange(state: 'connected' | 'disconnected' | 'error'): void {
    isConnected.value = state === 'connected'
    if (state === 'error') {
      error.value = 'Connection lost'
    } else if (state === 'connected') {
      error.value = null
    }
  }

  function markAsRead(notificationId: number): void {
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification) {
      notification.read = true
      updateUnreadCount()
    }
  }

  function markAllAsRead(): void {
    notifications.value.forEach(n => {
      n.read = true
    })
    updateUnreadCount()
  }

  function updateUnreadCount(): void {
    unreadCount.value = notifications.value.filter(n => !n.read).length
  }

  function cleanup(): void {
    if (unsubscribeNotification) {
      unsubscribeNotification()
      unsubscribeNotification = null
    }
    if (unsubscribeConnection) {
      unsubscribeConnection()
      unsubscribeConnection = null
    }
  }

  // Setup listeners
  onMounted(() => {
    unsubscribeNotification = onNotification(handleNewNotification)
    unsubscribeConnection = onConnectionChange(handleConnectionChange)
    isConnected.value = getConnectionStatus()
  })

  onUnmounted(() => {
    cleanup()
  })

  // Computed
  const hasUnread = computed(() => unreadCount.value > 0)
  const urgentNotifications = computed(() =>
    notifications.value.filter(n => n.priority >= 8 && !n.read)
  )

  return {
    // State
    notifications,
    unreadCount,
    hasUnread,
    urgentNotifications,
    isConnected,
    isLoading,
    error,

    // Actions
    initialize,
    loadHistory,
    markAsRead,
    markAllAsRead,
    disconnect,
    clearNotifications,
  }
}

// Auto-initialize composable for use in App.vue
export function useNotificationAutoInit() {
  const { initialize, isConnected, unreadCount, error } = useNotifications()

  onMounted(async () => {
    if (isAuthenticated()) {
      await initialize()
    }
  })

  return {
    isConnected,
    unreadCount,
    error,
  }
}
